import { useEffect, useMemo, useRef, useState } from 'react';
import './ProfileTask.scss';
import { TaskForm } from '../components/TaskForm/TaskForm';
import plusCircleIcon from '../../../../assets/img/button-icons/plus-circle-purple.svg';
import actionAlertIcon from '../../../../assets/img/button-icons/action-alert.svg';
import { useSelector } from 'react-redux';
import { TaskList } from '../components/TaskList/TaskList';
import { useForm } from 'react-hook-form';
import { TaskAssigneeType } from '../components/taskType';
import moment from 'moment';
import {
  useGetHospitalAllClinicians,
  useGetMyTeams,
  useGetTeams,
} from '../../teams/hooks';
import { getTeamOptions } from '../../teams/TeamListing';
import { translateTeamToOptions } from '../components/TaskForm/helper';
import { useUpdateActionStatus, useDeleteAction } from '../components/hooks';
import { useToast } from '../../../../context/ToastContext';
import { isBeforeTodayDateString } from '../../../helpers';
import { LoadingSpinner } from '../../LoadingSpinner';
import {
  useTriggerUpdate,
  SystemPermission,
  useLoggedInUser,
} from '../../../../context';
import { extractTeamOrHospitalId } from '../utils/utils';
import CaremapprConfirmation from '../../Confirmation/CaremapprConfirmation';

const defaultTaskValue = {
  taskName: null,
  taskType: TaskAssigneeType.INDIVIDUAL,
  assignedTeam: null,
  assignedPerson: null,
  description: '',
  dueDate: '',
  actionId: '',
  assignedTeamId: null,
  assignedPersonId: null,
  taskNameId: null,
};

export const translateTaskTemplatesToOptions = (taskTemplates) => {
  return taskTemplates.map((taskTemplate) => {
    return {
      value: taskTemplate.task_name,
      label: taskTemplate.task_name,
    };
  });
};

export const ProfileTask = ({ tasks, clinics }) => {
  const { loggedInUser } = useLoggedInUser();
  const { hiddenTasks } = useSelector((state) => state.preferences);
  const { triggerNotificationUpdate } = useTriggerUpdate();
  const [taskOpen, setTaskOpen] = useState(false);
  const [currentEditingTask, setCurrentEditingTask] = useState(null);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState(null);
  const { postGetTeams, teams, isLoading: isLoadingTeams } = useGetTeams();
  const { myAccessableTasks } = useGetMyTeams();
  const {
    postGetHospitalAllClinicians,
    cliniciansByHospital,
    isLoading: isLoadingAllClinicians,
  } = useGetHospitalAllClinicians();
  const { postUpdateActionStatus } = useUpdateActionStatus();
  const { postDeleteAction } = useDeleteAction();
  const { setToast } = useToast();
  const { teamId, hospitalId } = extractTeamOrHospitalId(clinics?.[0]);
  const hospitalIds = useMemo(() => {
    if (!Array.isArray(clinics)) return [];
    return clinics
      .map((clinic) => clinic?.id)
      .filter((id) => typeof id === 'number');
  }, [clinics]);
  const hospitalIdsKey = useMemo(() => hospitalIds.join('|'), [hospitalIds]);
  const contentContainer = useRef(null);
  const clinicianOptions = getTeamOptions(
    cliniciansByHospital,
    teamId || hospitalId,
  );
  const teamsOptions = translateTeamToOptions(teams);
  const taskNameOptions = useMemo(() => {
    const accessableTasks = translateTaskTemplatesToOptions(
      myAccessableTasks,
    ).filter((task) => !hiddenTasks.includes(task.value));
    const editingTask = currentEditingTask?.description
      ? {
          label: currentEditingTask.description,
          value: currentEditingTask.description,
        }
      : null;
    const all = [...accessableTasks, editingTask].filter(Boolean);
    const seen = new Set();
    return all.filter((opt) => {
      if (!opt?.value) return false;
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [myAccessableTasks, hiddenTasks, currentEditingTask]);

  const taskForm = useForm({
    defaultValues: defaultTaskValue,
  });

  const { actions } = useSelector((state) => state.actions);
  const unCompletedTasks = actions.filter(
    (task) =>
      (!task.ended_at && !hiddenTasks.includes(task.description)) ||
      (!task.ended_at && loggedInUser.userId === task.clinician_id),
  );
  const hasActionDueToday = unCompletedTasks.some((task) =>
    isBeforeTodayDateString(task.due_date),
  );

  const { selectedTenancy } = useSelector(
    (state) => state.clinicianProfile,
    //(state: RootState) => state.clinicianProfile, // if we TSify this file
  );

  const onAddNewTask = () => {
    taskForm.reset(defaultTaskValue);
    setTaskFormOpen(true);
  };

  const onEditTask = (task) => {
    setTaskFormOpen(true);
    setCurrentEditingTask(task);

    contentContainer.current.scroll({
      top: 0,
      behavior: 'smooth',
    });

    taskForm.reset(
      {
        taskNameId: task.description ?? null,
        taskType: task.clinic_id
          ? TaskAssigneeType.TEAM
          : TaskAssigneeType.INDIVIDUAL,
        assignedTeamId: task.clinic_id ?? null,
        assignedPersonId: task.clinician_id ?? null,
        description: task?.details?.description ?? '',
        dueDate: task.due_date
          ? moment(task.due_date).format('YYYY-MM-DD')
          : '',
        actionId: task.id,
      },
      { keepDirty: true },
    );
  };

  const onCompletedTask = async (taskId) => {
    setToast('Completing task...');
    await postUpdateActionStatus({
      markAsCompleted: true,
      actionId: taskId,
    });
    setToast('Task completed!');
    triggerNotificationUpdate();
  };

  const onUncompleteTask = async (taskId) => {
    setToast('Uncompleting task...');
    await postUpdateActionStatus({
      markAsCompleted: false,
      actionId: taskId,
    });
    setToast('Task uncompleted!');
    triggerNotificationUpdate();
  };

  const onDeleteTask = (taskId) => {
    setConfirmDeleteTaskId(taskId);
  };

  const handleConfirmDelete = async () => {
    if (confirmDeleteTaskId) {
      setToast('Deleting task...');
      await postDeleteAction(confirmDeleteTaskId);
      setToast('Task deleted!');
      triggerNotificationUpdate();
    }
    setConfirmDeleteTaskId(null);
  };

  useEffect(() => {
    if (hospitalIds.length > 0) {
      postGetTeams(hospitalIds);
    }
    if (hospitalId) {
      postGetHospitalAllClinicians([hospitalId]);
    }
  }, [hospitalIdsKey, hospitalId]);

  const canAssignTask =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[SystemPermission.ASSIGN_TASK]);

  return (
    <div className="profile-task-container">
      <div className="header" onClick={() => setTaskOpen((open) => !open)}>
        {taskOpen ? (
          <i className="fa fa-minus" aria-hidden="true" />
        ) : (
          <i className="fa fa-plus" aria-hidden="true" />
        )}
        Tasks
        {isLoadingAllClinicians || isLoadingTeams ? (
          <div className="mx-1" style={{ transform: 'scale(0.6)' }}>
            <LoadingSpinner color="white" showText="" />
          </div>
        ) : (
          <span className="profile-task-count">{unCompletedTasks.length}</span>
        )}
        {hasActionDueToday && (
          <span className="profile-task-alert">
            <img src={actionAlertIcon} alt="has task due today icon" />
          </span>
        )}
      </div>
      {taskOpen ? (
        <div className="content" ref={contentContainer}>
          {canAssignTask &&
            (!taskFormOpen ? (
              <div className="add-task-container">
                <button type="button" onClick={onAddNewTask}>
                  Add task <img src={plusCircleIcon} alt="add task icon" />
                </button>
              </div>
            ) : (
              <div className="add-task-form-container">
                <TaskForm
                  clinicianOptions={clinicianOptions}
                  teamsOptions={teamsOptions}
                  taskNameOptions={taskNameOptions}
                  isOptionsLoading={isLoadingAllClinicians || isLoadingTeams}
                  form={taskForm}
                  onClose={() => {
                    setTaskFormOpen(false);
                    setCurrentEditingTask(null);
                  }}
                />
              </div>
            ))}
          <div className="task-list-container">
            <TaskList
              tasks={actions}
              onEditTask={onEditTask}
              onCompletedTask={onCompletedTask}
              onUncompleteTask={onUncompleteTask}
              onDeleteTask={onDeleteTask}
              canAssignTask={canAssignTask}
            />
          </div>
        </div>
      ) : null}
      <CaremapprConfirmation
        open={!!confirmDeleteTaskId}
        text="Are you sure you want to delete this task?"
        icon="warning"
        confirmButtonText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteTaskId(null)}
        onClose={() => setConfirmDeleteTaskId(null)}
      />
    </div>
  );
};
